'use client'

import * as React from 'react'
import { tw } from 'utils/classnames'
import { useUserQuery } from 'auth/hooks'
import { isLoggedInQuerySelector } from 'auth/selectors'
import type { PDPAsset, PDPCategory } from 'cf-grpc/creativefabrica/content/v1/types_pb'
import { useLocale, useTranslations } from 'next-intl'
import { mapLocaleToContentLanguage } from 'cf-i18n/constants'
import { ContentLanguage } from 'codegen/types'

import { useContentFetchCategoryQuery, useContentLicenseCountQuery } from '@web/gen/content'
import { DetailsWithAccess } from './with-access'
import { DetailsWithoutAccess } from './no-access'
import { ProductBuyButton } from '../buy'
import { getAssetsDownloadOptions } from './util'
import {
  useNeedsPaymentState,
  useWasDownloadedState,
} from '@web/app/[locale]/(web)/prod/[slug]/product-asset-context'
import { UnderlineCheck } from '@web/components/icons/underline-check'
import { TwoCoinsIcon } from '@web/components/icons/two-coins'
import { useIsUserAllAccessTrialingQuery } from '@repo/cf-user/hooks/use-user-data'
import { getIsProductFree } from '@web/components/content-api/config'
import { useIsFirstDownload } from '@web/hooks/use-product-data'

interface DownloadWidgetProps {
  className?: string
  assets: PDPAsset[]
  category: PDPCategory | undefined
}

const FREE_PRODUCTS_BEFORE_UPGRADE = 10

// oxlint-disable-next-line complexity
export function DownloadWidget({ className, category, assets }: DownloadWidgetProps) {
  const t = useTranslations('prod.product_description_page.buy')
  const locale = useLocale()
  const contentLanguage = mapLocaleToContentLanguage.get(locale) || ContentLanguage.LanguageEn
  const [wasDownloaded] = useWasDownloadedState()
  const [paymentUrl] = useNeedsPaymentState()
  const isFreeProduct = getIsProductFree(category?.root?.id)
  const { isPending, data: userData } = useUserQuery(undefined, {
    select: data => data?.me?.user,
  })
  const isLoggedIn =
    !isPending &&
    isLoggedInQuerySelector({
      me: { user: userData },
    })
  const isAllAccess = !isPending && !!userData?.subscriptionAccess.allAccess.isSubscribed
  const { data: categoryData, isPending: isCategoryLoading } = useContentFetchCategoryQuery({
    id: category?.root?.id || '',
    language: contentLanguage,
  })
  const { data: isFreeTrial } = useIsUserAllAccessTrialingQuery()

  const { data: licenseCount } = useContentLicenseCountQuery(undefined, {
    enabled: isFreeTrial,
    select: data => {
      if (!data) {
        return 0
      }
      const { contentLicenseCount } = data
      return contentLicenseCount
    },
  })
  const { isFirstDownload } = useIsFirstDownload(
    isLoggedIn && licenseCount === FREE_PRODUCTS_BEFORE_UPGRADE - 1,
  )

  const renderDetails = () => {
    if (isPending || isCategoryLoading) {
      return <div className="bg-cf-dreamy-cloud-300 m-4 h-[124px] animate-pulse" />
    }
    if (isLoggedIn && isAllAccess) {
      return <DetailsWithAccess assets={assets} category={category} />
    }

    return (
      <DetailsWithoutAccess
        totalProducts={categoryData?.contentFetchCategory?.totalProducts || 0}
        rootCategoryId={category?.root?.id || ''}
      />
    )
  }

  const downloadableAssets = getAssetsDownloadOptions(category?.root?.id || '', assets)
  const hasMultipleDownloadOptions = downloadableAssets.length > 1
  const showBorder = hasMultipleDownloadOptions || (!hasMultipleDownloadOptions && !isAllAccess)

  return (
    <div
      id="prod-download"
      className={tw(
        showBorder && 'border-cf-dreamy-cloud-600 rounded-4 relative border pb-4',
        className,
      )}
    >
      {showBorder ? renderDetails() : null}
      <ProductBuyButton
        className={tw(
          showBorder
            ? 'mx-4 w-[calc(100%-30px)] min-[600px]:w-[calc(100%-30px)]'
            : 'my-8 w-full min-[600px]:w-full',
          wasDownloaded && 'mt-4',
        )}
        disabled={!!paymentUrl}
      />
      {paymentUrl ? (
        <div className="text-12 m-4 mb-0 text-red-100">
          {t.rich('payment_failed', {
            a: text => (
              <a className="font-bold underline" href={paymentUrl} target="_blank" rel="noreferrer">
                {text}
              </a>
            ),
          })}
        </div>
      ) : null}
      {isFreeTrial &&
      !isFreeProduct &&
      isFirstDownload &&
      licenseCount === FREE_PRODUCTS_BEFORE_UPGRADE - 1 ? (
        <div className="text-12 mx-4 mt-4 flex flex-row gap-2">
          <TwoCoinsIcon className="shrink-0" />
          <span>{t('last_free')}</span>
        </div>
      ) : null}
      {wasDownloaded ? (
        <div className="text-12 m-4 mb-0 flex items-center gap-2">
          <UnderlineCheck /> {t('already_downloaded')}
        </div>
      ) : null}
    </div>
  )
}
